import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Stack,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../../../../../auth/AuthContext';
import { auth } from '../../../../../firebase';
import { getApiBase } from '../../../../../utils/apiConfig';

// Fits the map to the combined bounds of the original + clipped rings whenever
// the preview data changes. Kept as its own component so it can call useMap().
const FitBoundsToRings = ({ rings }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !rings?.length) return;

    let bounds = null;
    rings.forEach((ring) => {
      [ring.original_geojson, ring.clipped_geojson].forEach((geojson) => {
        if (!geojson) return;
        try {
          const layerBounds = L.geoJSON(geojson).getBounds();
          if (layerBounds.isValid()) {
            bounds = bounds ? bounds.extend(layerBounds) : layerBounds;
          }
        } catch (e) {
          // Ignore malformed geometry when computing bounds
        }
      });
    });

    if (bounds && bounds.isValid() && map.getPane('mapPane')) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, rings]);

  return null;
};

const ORIGINAL_RING_STYLE = { color: '#1976d2', weight: 2, fillOpacity: 0.05, dashArray: '4 4' };
const CLIPPED_RING_STYLE = { color: '#2e7d32', weight: 2, fillOpacity: 0.25 };

const BoundaryConstrainedProcessor = () => {
  const { user } = useAuth();

  // School search / selection
  const [schoolQuery, setSchoolQuery] = useState('');
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [schoolLoading, setSchoolLoading] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);

  // Base travel mode + candidates
  const [baseTravelMode, setBaseTravelMode] = useState('drive');
  const [candidates, setCandidates] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);

  // Boundary selection: 'state' | 'county' | 'district:<geoid>'
  const [boundarySelection, setBoundarySelection] = useState('');

  // Drive time selection
  const [selectedDriveTimes, setSelectedDriveTimes] = useState([]);

  // Preview
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Process
  const [servicesToRun, setServicesToRun] = useState({
    esri: true,
    nearby_schools: true,
    metrics: true,
    ai_summaries: true
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processError, setProcessError] = useState(null);

  const getAuthHeaders = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return {};
    try {
      const token = await firebaseUser.getIdToken();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.error('Error getting auth token:', error);
      return {};
    }
  };

  // Debounced school search against the same search endpoint EnhancedStandardProcessor's
  // reprocessing flow relies on.
  useEffect(() => {
    if (schoolQuery.trim().length < 2) {
      setSchoolOptions([]);
      return;
    }
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setSchoolLoading(true);
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(
          `${getApiBase()}/v2/cloud-run/school/search?q=${encodeURIComponent(schoolQuery.trim())}&limit=20`,
          { headers: authHeaders, credentials: 'include' }
        );
        if (!cancelled && response.ok) {
          const data = await response.json();
          setSchoolOptions(data.schools || []);
        }
      } catch (error) {
        console.error('School search failed:', error);
      } finally {
        if (!cancelled) setSchoolLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [schoolQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetDownstreamState = () => {
    setCandidates(null);
    setCandidatesError(null);
    setBoundarySelection('');
    setSelectedDriveTimes([]);
    setPreviewData(null);
    setPreviewError(null);
    setProcessResult(null);
    setProcessError(null);
  };

  const loadCandidates = useCallback(async (school, travelMode) => {
    if (!school) return;
    setCandidatesLoading(true);
    setCandidatesError(null);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        `${getApiBase()}/v2/boundary-workflow/school/${school.uuid}/candidates?base_travel_mode=${travelMode}`,
        { headers: authHeaders, credentials: 'include' }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Failed to load boundary candidates: ${response.status}`);
      }
      setCandidates(data);
      setSelectedDriveTimes(data.available_drive_times || []);
    } catch (error) {
      console.error('Failed to load boundary candidates:', error);
      setCandidatesError(error.message);
      setCandidates(null);
    } finally {
      setCandidatesLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
    resetDownstreamState();
    if (school) {
      loadCandidates(school, baseTravelMode);
    }
  };

  const handleBaseTravelModeChange = (mode) => {
    setBaseTravelMode(mode);
    resetDownstreamState();
    if (selectedSchool) {
      loadCandidates(selectedSchool, mode);
    }
  };

  // Parse the currently selected boundary into {boundary_type, boundary_geoid, name}
  const selectedBoundary = useMemo(() => {
    if (!boundarySelection || !candidates) return null;
    if (boundarySelection === 'state' && candidates.boundaries?.state) {
      return { boundary_type: 'state', boundary_geoid: candidates.boundaries.state.geoid, name: candidates.boundaries.state.name };
    }
    if (boundarySelection === 'county' && candidates.boundaries?.county) {
      return { boundary_type: 'county', boundary_geoid: candidates.boundaries.county.geoid, name: candidates.boundaries.county.name };
    }
    if (boundarySelection.startsWith('district:')) {
      const geoid = boundarySelection.slice('district:'.length);
      const district = (candidates.boundaries?.districts || []).find((d) => d.geoid === geoid);
      if (district) {
        return { boundary_type: 'district', boundary_geoid: district.geoid, name: district.name };
      }
    }
    return null;
  }, [boundarySelection, candidates]);

  const targetTravelMode = useMemo(() => {
    if (!selectedBoundary) return null;
    const base = baseTravelMode === 'walk' ? 'walk' : 'drive';
    return `${base}_${selectedBoundary.boundary_type}`;
  }, [selectedBoundary, baseTravelMode]);

  const toggleDriveTime = (driveTime) => {
    setSelectedDriveTimes((prev) =>
      prev.includes(driveTime) ? prev.filter((dt) => dt !== driveTime) : [...prev, driveTime].sort((a, b) => a - b)
    );
  };

  const handlePreview = async () => {
    if (!selectedSchool || !selectedBoundary || selectedDriveTimes.length === 0) return;

    setPreviewLoading(true);
    setPreviewError(null);
    setProcessResult(null);
    setProcessError(null);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(`${getApiBase()}/v2/boundary-workflow/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          school_uuid: selectedSchool.uuid,
          boundary_type: selectedBoundary.boundary_type,
          boundary_geoid: selectedBoundary.boundary_geoid,
          base_travel_mode: baseTravelMode,
          drive_times: selectedDriveTimes
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Preview failed: ${response.status}`);
      }
      setPreviewData(data);
    } catch (error) {
      console.error('Boundary preview failed:', error);
      setPreviewError(error.message);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleServiceToggle = (key) => {
    setServicesToRun((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProcess = async () => {
    if (!selectedSchool || !selectedBoundary || !previewData) return;

    setIsProcessing(true);
    setProcessError(null);
    setProcessResult(null);
    try {
      const authHeaders = await getAuthHeaders();
      const selectedServices = Object.keys(servicesToRun).filter((key) => servicesToRun[key]);
      const response = await fetch(`${getApiBase()}/v2/boundary-workflow/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          school_uuid: selectedSchool.uuid,
          boundary_type: selectedBoundary.boundary_type,
          boundary_geoid: selectedBoundary.boundary_geoid,
          base_travel_mode: baseTravelMode,
          drive_times: selectedDriveTimes,
          services: selectedServices
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Processing failed: ${response.status}`);
      }
      setProcessResult(data);
    } catch (error) {
      console.error('Boundary-constrained processing failed:', error);
      setProcessError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const boundaries = candidates?.boundaries;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        Boundary-Constrained Polygon Workflow
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Clip a school's drive-time rings to a state, county, or school district boundary, preview the
        result, then re-run ESRI enrichment, nearby schools, metrics, and AI summaries for the
        boundary-constrained study area. Results are stored alongside the standard drive-time data
        under a separate travel mode (e.g. <code>drive_county</code>) — nothing is overwritten.
      </Typography>

      {/* Step 1: School selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>1. Select a School</Typography>
          <Autocomplete
            options={schoolOptions}
            loading={schoolLoading}
            value={selectedSchool}
            getOptionLabel={(option) => option.name ? `${option.name} (${option.ncessch})` : option.ncessch || ''}
            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
            onChange={(event, newValue) => handleSchoolSelect(newValue)}
            onInputChange={(event, newInputValue) => setSchoolQuery(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by school name or NCES ID..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {schoolLoading && <CircularProgress size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            noOptionsText={schoolQuery.length < 2 ? 'Type at least 2 characters to search' : 'No schools found'}
          />

          {selectedSchool && (
            <Box sx={{ mt: 2 }}>
              <FormControl size="small">
                <InputLabel>Base Travel Mode</InputLabel>
                <Select
                  label="Base Travel Mode"
                  value={baseTravelMode}
                  onChange={(e) => handleBaseTravelModeChange(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="drive">Drive Time</MenuItem>
                  <MenuItem value="walk">Walk Time</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Boundary selection */}
      {selectedSchool && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>2. Choose a Boundary</Typography>

            {candidatesLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading boundary candidates...</Typography>
              </Box>
            )}

            {candidatesError && <Alert severity="error">{candidatesError}</Alert>}

            {boundaries && !candidatesLoading && (
              <>
                {(candidates.available_drive_times || []).length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No existing ESRI {baseTravelMode}-time data found for this school. Run the standard
                    workflow first before using the boundary-constrained workflow.
                  </Alert>
                )}

                <RadioGroup value={boundarySelection} onChange={(e) => { setBoundarySelection(e.target.value); setPreviewData(null); }}>
                  {boundaries.state && (
                    <FormControlLabel
                      value="state"
                      control={<Radio />}
                      label={`State: ${boundaries.state.name}`}
                    />
                  )}
                  {boundaries.county && (
                    <FormControlLabel
                      value="county"
                      control={<Radio />}
                      label={`County: ${boundaries.county.name}`}
                    />
                  )}
                  {!boundaries.state && !boundaries.county && (candidates.boundaries?.districts || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No state, county, or district boundaries could be resolved for this school's location.
                    </Typography>
                  )}
                  {(boundaries.districts || []).map((district) => (
                    <FormControlLabel
                      key={district.geoid}
                      value={`district:${district.geoid}`}
                      control={<Radio />}
                      label={`District: ${district.name}`}
                    />
                  ))}
                </RadioGroup>

                {selectedBoundary && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Drive-time rings to clip (target travel mode: <Chip size="small" label={targetTravelMode} />)
                    </Typography>
                    <FormGroup row>
                      {(candidates.available_drive_times || []).map((dt) => (
                        <FormControlLabel
                          key={dt}
                          control={
                            <Checkbox
                              checked={selectedDriveTimes.includes(dt)}
                              onChange={() => toggleDriveTime(dt)}
                            />
                          }
                          label={`${dt} min`}
                        />
                      ))}
                    </FormGroup>

                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={handlePreview}
                      disabled={previewLoading || selectedDriveTimes.length === 0}
                      sx={{ mt: 1 }}
                    >
                      {previewLoading ? 'Clipping...' : 'Preview Clipped Polygon'}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {previewError && <Alert severity="error" sx={{ mb: 3 }}>{previewError}</Alert>}

      {previewData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon /> 3. Preview: Original vs. Clipped Rings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Boundary: <strong>{previewData.boundary_name}</strong> ({previewData.boundary_type}) — dashed blue
              is the original {baseTravelMode}-time ring, solid green is the boundary-clipped result that will be
              sent to ESRI.
            </Typography>

            <Box sx={{ height: 420, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <MapContainer center={[39.8, -98.5]} zoom={4} style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  url="https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>'
                />
                <FitBoundsToRings rings={previewData.rings} />
                {previewData.rings.map((ring) => (
                  <React.Fragment key={ring.drive_time}>
                    {ring.original_geojson && (
                      <GeoJSON
                        key={`orig-${ring.drive_time}`}
                        data={ring.original_geojson}
                        style={() => ORIGINAL_RING_STYLE}
                      />
                    )}
                    {ring.clipped_geojson && (
                      <GeoJSON
                        key={`clip-${ring.drive_time}`}
                        data={ring.clipped_geojson}
                        style={() => CLIPPED_RING_STYLE}
                      />
                    )}
                  </React.Fragment>
                ))}
              </MapContainer>
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Drive Time</TableCell>
                    <TableCell>ESRI Data Available</TableCell>
                    <TableCell>Intersects Boundary</TableCell>
                    <TableCell>Area Retained</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.rings.map((ring) => (
                    <TableRow key={ring.drive_time}>
                      <TableCell>{ring.drive_time} min</TableCell>
                      <TableCell>
                        {ring.available ? <CheckCircleIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        {ring.clipped ? <CheckCircleIcon color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell>{ring.clipped ? `${Math.round(ring.area_retained_pct)}%` : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>4. Confirm &amp; Process</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Sends the clipped ring(s) shown above to ArcGIS GeoEnrichment, then re-runs the standard
              workflow's downstream steps against the boundary-constrained study area.
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={servicesToRun.esri} onChange={() => handleServiceToggle('esri')} />}
                label="ESRI GeoEnrichment"
              />
              <FormControlLabel
                control={<Checkbox checked={servicesToRun.nearby_schools} onChange={() => handleServiceToggle('nearby_schools')} />}
                label="Nearby Schools"
              />
              <FormControlLabel
                control={<Checkbox checked={servicesToRun.metrics} onChange={() => handleServiceToggle('metrics')} />}
                label="Metrics"
              />
              <FormControlLabel
                control={<Checkbox checked={servicesToRun.ai_summaries} onChange={() => handleServiceToggle('ai_summaries')} />}
                label="AI Summaries"
              />
            </FormGroup>

            <Button
              variant="contained"
              color="primary"
              startIcon={<PlayArrowIcon />}
              onClick={handleProcess}
              disabled={isProcessing || !previewData.rings.some((r) => r.clipped)}
            >
              {isProcessing ? 'Processing...' : `Process Boundary-Constrained Study Area (${targetTravelMode})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {processError && <Alert severity="error" sx={{ mb: 3 }}>{processError}</Alert>}

      {processResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" /> Processing Complete
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Target Travel Mode</Typography>
                <Typography variant="body1">{processResult.target_travel_mode}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">ESRI Saved</Typography>
                <Typography variant="body1">{processResult.esri_saved ? 'Yes' : 'Skipped'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Metrics Calculated</Typography>
                <Typography variant="body1">
                  {processResult.metrics_calculated === true ? 'Yes' : processResult.metrics_calculated === false ? 'Failed' : 'Skipped'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">AI Summaries</Typography>
                <Typography variant="body1">{processResult.ai_summaries_started ? 'Started in background' : 'Skipped'}</Typography>
              </Grid>
            </Grid>

            {processResult.nearby_school_counts && Object.keys(processResult.nearby_school_counts).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Nearby Schools Found</Typography>
                <Stack direction="row" spacing={1}>
                  {Object.entries(processResult.nearby_school_counts).map(([driveTime, count]) => (
                    <Chip key={driveTime} label={`${driveTime} min: ${count} schools`} size="small" />
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {!user && (
        <Alert severity="warning" sx={{ mt: 3 }}>Not authenticated — sign in to use this workflow.</Alert>
      )}
    </Box>
  );
};

export default BoundaryConstrainedProcessor;
